<template>
  <a
    href="#main"
    class="sr-only sr-only-focusable skip-link"
    @click="scrollToMain($event)"
    >Skip to main content</a
  >
  <div class="container">
    <!-- Top header starts -->
    <header class="row">
      <div class="col-12">
        <div class="grid-container">
          <a :href="MCD_DASHBOARD_URL">{{
            $t("CONTENT.HOMEPAGE.HEADER_LINK_TEXT")
          }}</a>
        </div>
      </div>
    </header>
    <!-- Top header ends -->

    <!-- Content section starts -->

    <main id="main" class="content" tabindex="0" role="main">
      <!-- Logo row starts -->
      <div class="row">
        <div class="col-12">
          <!-- Header logo starts -->
          <img
            src="../../src/assets/images/mc-logo.svg"
            width="100"
            height="35"
            alt="MC logo"
          />
          <!-- Header logo ends -->
          <h1>{{ $t("CONTENT.HOMEPAGE.HEADING") }}</h1>
          <p>
            {{ $t("CONTENT.HOMEPAGE.DESCRIPTION") }}
          </p>
        </div>
      </div>
      <!-- Logo row ends -->

      <!-- Lower environment content starts -->
      <div
        class="row lower-env"
        v-if="CURRENT_ENV.VUE_APP_ENV_MODE === LOWER_ENV"
      >
        <div class="col-12">
          <ul class="column-container">
            <!-- Column starts -->
            <li v-for="(env, index) in lowerEnvData" :key="index">
              <section :aria-labelledby="'lower-env-' + index">
                <div
                  class="content-column lower-env-container"
                  data-automation="mtf-col"
                >
                  <!-- Top content starts -->
                  <div class="top-content">
                    <h2 :id="'lower-env-' + index">
                      {{ env.name }} Environment
                    </h2>
                    <p>
                      Create and manage your business profile in
                      {{ env.name }} environment.<br aria-hidden="true" /><br
                        aria-hidden="true"
                      />
                    </p>
                  </div>
                  <!-- Top content ends -->

                  <!-- Bottom button starts -->
                  <div class="button-row">
                    <button
                      :aria-describedby="'go-to-env-' + index"
                      @click="redirectToEnvironment(env.link)"
                    >
                      Go to {{ env.name }}
                    </button>
                  </div>
                  <span
                    :id="'go-to-env-' + index"
                    class="a11y-hidden"
                    aria-hidden="true"
                    >Click this button to go to {{ env.name }} environment</span
                  >
                  <!-- Bottom button ends -->
                </div>
              </section>
            </li>
            <!-- Column ends -->
          </ul>
        </div>
      </div>
      <!-- Lower environment content ends -->

      <!-- Higher environemnt block starts -->
      <div
        class="row higher-env"
        v-if="CURRENT_ENV.VUE_APP_ENV_MODE === HIGHER_ENV"
      >
        <div class="col-12">
          <div class="column-container">
            <!-- Column 1 starts -->
            <a class="content-column" :href="MTF_URL" data-automation="mtf-col">
              <!-- Top content starts -->
              <div class="top-content">
                <!-- Image starts -->
                <img
                  src="../../src/assets/images/test-environment.svg"
                  width="36"
                  height="36"
                  alt="MTF Environment image"
                />
                <!-- Image ends -->
                <h2>{{ $t("CONTENT.HOMEPAGE.MTF.HEADING") }}</h2>
                <p>
                  {{ $t("CONTENT.HOMEPAGE.MTF.DESCRIPTION_LINE_1") }}
                </p>
              </div>
              <!-- Top content ends -->

              <!-- Bottom button starts -->
              <div class="button-row">
                <button tabindex="-1">
                  {{ $t("CONTENT.HOMEPAGE.MTF.ACTION_TEXT") }}
                </button>
              </div>
              <!-- Bottom button ends -->
            </a>
            <!-- Column 1 ends -->

            <!-- Column 2 starts -->
            <a
              class="content-column"
              :href="PROD_URL"
              data-automation="prod-col"
            >
              <!-- Top content starts -->
              <div class="top-content">
                <!-- Image starts -->
                <img
                  src="../../src/assets/images/prod-environment.svg"
                  width="36"
                  height="36"
                  alt="Production Environment image"
                />
                <!-- Image ends -->
                <h2>{{ $t("CONTENT.HOMEPAGE.PROD.HEADING") }}</h2>
                <p>
                  {{ $t("CONTENT.HOMEPAGE.PROD.DESCRIPTION_LINE_1") }}
                </p>
              </div>
              <!-- Top content ends -->

              <!-- Bottom button starts -->
              <div class="button-row">
                <button tabindex="-1">
                  {{ $t("CONTENT.HOMEPAGE.PROD.ACTION_TEXT") }}
                </button>
              </div>
              <!-- Bottom button ends -->
            </a>
            <!-- Column 2 ends -->
          </div>
        </div>
      </div>
      <!-- Higher environemnt block ends -->
    </main>
    <!-- Content section ends -->
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "Home",
  components: {},
  data() {
    return {
      MCD_DASHBOARD_URL: process.env.VUE_APP_MCD_DASHBOARD_URL,
      MTF_URL: process.env.VUE_APP_MTF_URL,
      PROD_URL: process.env.VUE_APP_PROD_URL,
      CURRENT_ENV: process.env,
      LOWER_ENV: "LOWER",
      HIGHER_ENV: "HIGHER",
      lowerEnvData: [
        {
          name: "Stage",
          link: "https://stage.src.mastercard.com/srci-mp/dashboard/",
        },
        {
          name: "ITF",
          link: "https://stage.src.mastercard.com/srci-mp-itf/dashboard/",
        },
        {
          name: "PERF",
          link: "https://stage.src.mastercard.com/srci-mp-perf/dashboard/",
        },
      ],
    };
  },
  methods: {
    redirectToEnvironment(link: string) {
      window.location.href = link;
    },
    scrollToMain(event: MouseEvent): void {
      event.preventDefault();
      const el = document.getElementById("main");
      if (el) {
        el.focus();
        el.scrollIntoView({ behavior: "smooth" });
      }
    },
  },
});
</script>

<style lang="scss">
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background-color: #ffffff;
  color: #cf4500;
  padding: 4px;
  text-decoration: none;
  font-size: 14px;
  z-index: 10001;
  transition: top 0.2s ease-in-out;

  &:focus {
    top: 6px;
    outline: 2px solid #cf4500;
    outline-offset: 2px;
  }

  &:hover:focus {
    background: #ffffff;
  }
}

header {
  background-color: #141413;
  text-align: right;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 10000;
  width: 100%;
  height: 56px;

  .lower-env-container,
  a {
    color: #f0eeeb;
    text-decoration: none;
    letter-spacing: 0.5px;
    padding: 17px 16px 18px;
    display: inline-flex;
    margin-right: 15px;

    &:active,
    &:focus {
      outline: 2px solid #cf4500;
    }

    &:hover {
      border-bottom: 3px solid white;
    }
  }
}

.content {
  padding: 20px;
  text-align: center;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 56px;
  min-height: calc(100vh - 96px);
  font-size: 1.25rem;

  h1 {
    font-size: 2.3rem;
    font-weight: 200;
    margin: 10px 0;
  }
  .a11y-hidden {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
  .column-container {
    justify-content: center;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    margin-top: 30px;
    list-style: none;

    .content-column {
      border-radius: 6px;
      box-shadow: 0 1px 4px rgba(177, 173, 166, 0.57);
      padding: 30px 22px 40px 22px;
      margin: 10px auto;
      max-width: 218px;
      text-decoration: none;
      color: inherit;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      &:hover,
      &:active,
      &:focus {
        outline: 2px solid #cf4500;
      }

      img {
        margin: 15px 0;
      }

      h2 {
        margin: 0 0 10px 0;
        font-size: 1.25rem;
        font-family: "MarkOffcForMC-Bold";
        text-align: center;
      }

      p {
        text-align: center;
        margin: 0 0 20px 0;
        font-size: 1.15rem;
      }

      .button-row {
        button {
          background-color: #cf4500;
          color: white;
          padding: 10px 25px;
          border: none;
          border-radius: 20px;
          font-family: "MarkOffcForMC-Medium";
          margin: 30px auto 0 auto;
          font-size: 1.1rem;
          cursor: pointer;
          display: flex;
          width: 100%;
          align-items: center;
          justify-content: center;
          max-width: 200px;
        }
      }
    }
  }
}

@media (min-width: 600px) {
  .content {
    .column-container {
      flex-direction: row;
      .content-column {
        margin: 10px;
      }
    }
  }
}

@media (min-width: 1440px) {
  .grid-container {
    max-width: 1440px;
  }
}
</style>
